/**
 * Delete account.
 *
 * Apple requires deletion to be reachable in-app and never blocked by a
 * subscription. Apple-billed members get a cancel warning; website members
 * are told their membership is cancelled as part of delete (backend).
 * Title comes from the stack header.
 */
import {
  SettingsPanel,
  SettingsPanelBody,
} from '@/components/settings/SettingsPanel';
import Container from '@/components/ui/Container';
import { BodyText, CaptionText } from '@/components/ui/Typography';
import { useAuth } from '@/lib/AuthContext';
import { BRAND_COLORS as C } from '@/lib/brandColors';
import { isMemberTier } from '@/lib/membershipTier';
import {
  getActiveMembershipEntitlement,
  getCustomerInfo,
  getManagementUrl,
} from '@/lib/purchases/RevenueCat';
import { CONTENT_MAX_WIDTH, useIsMobile } from '@/lib/useDeviceType';
import { Ionicons } from '@expo/vector-icons';
import { Redirect, useRouter } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { Keyboard, Linking, Platform, Pressable, ScrollView } from 'react-native';
import type { CustomerInfo } from 'react-native-purchases';
import { Button, Input, Spinner, XStack, YStack } from 'tamagui';

const CONFIRM_WORD = 'DELETE';

const CONSEQUENCES = [
  'You lose access to every course, live session and the community.',
  'Courses bought for lifetime access cannot be restored later.',
  'Your progress, bookmarks and dashboard settings are removed.',
  'This cannot be undone. You would have to start a new account.',
];

export default function DeleteAccountScreen() {
  // Native-only (Apple 5.1.1(v)): on web the route is still a reachable URL,
  // so bounce direct visits back to Account — web deletions go via support.
  if (Platform.OS === 'web') {
    return <Redirect href="/settings" />;
  }

  return <DeleteAccountScreenNative />;
}

function DeleteAccountScreenNative() {
  const router = useRouter();
  const isMobile = useIsMobile();
  const { membershipTier, deleteAccount } = useAuth();

  const [info, setInfo] = useState<CustomerInfo | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getCustomerInfo().then((i) => {
      if (alive) setInfo(i);
    });
    return () => {
      alive = false;
    };
  }, []);

  const goBack = () =>
    router.canGoBack() ? router.back() : router.replace('/settings');

  const appleSubscription = getActiveMembershipEntitlement(info);
  const hasMembership = isMemberTier(membershipTier);
  const confirmed = confirmation.trim().toUpperCase() === CONFIRM_WORD;
  const sectionGap = isMobile ? '$3' : '$4';

  const runDelete = async () => {
    setDeleting(true);
    setError(null);

    const { error: deleteError } = await deleteAccount();

    if (deleteError) {
      setDeleting(false);
      setError(
        deleteError.message ||
          'Could not delete your account. Please contact support.',
      );
      return;
    }
  };

  return (
    <YStack f={1} bg={C.pageBg}>
      <ScrollView
        contentContainerStyle={{ paddingBottom: isMobile ? 40 : 56 }}
        showsVerticalScrollIndicator={false}
        // Keep the DELETE button reachable while typing the confirmation:
        // the inset pushes content above the keyboard, drag/tap dismiss it.
        automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
        keyboardDismissMode="on-drag"
        keyboardShouldPersistTaps="handled"
      >
        <Pressable onPress={Keyboard.dismiss} accessible={false}>
        <Container
          maxWidth={CONTENT_MAX_WIDTH}
          centered
          py={isMobile ? '$4' : '$6'}
        >
          <YStack gap={sectionGap} w="100%">
            <CaptionText color={C.muted} ta="left">
              This permanently closes your Open Studio account.
            </CaptionText>

            <SettingsPanel title="What happens">
              <SettingsPanelBody gap="$2.5">
                {CONSEQUENCES.map((line) => (
                  <XStack key={line} gap="$2.5" ai="flex-start">
                    <YStack pt={3} flexShrink={0}>
                      <Ionicons
                        name="remove-circle-outline"
                        size={18}
                        color={C.error}
                      />
                    </YStack>
                    <BodyText color={C.muted} f={1} ta="left">
                      {line}
                    </BodyText>
                  </XStack>
                ))}
              </SettingsPanelBody>
            </SettingsPanel>

            {hasMembership ? (
              appleSubscription ? (
                <SettingsPanel
                  title="Your membership is still active"
                  subtitle="Billed by Apple. Deleting the account does not cancel it."
                >
                  <SettingsPanelBody>
                    <BodyText color={C.muted} ta="left">
                      Cancel the subscription in your Apple ID settings first,
                      otherwise Apple keeps charging you after the account is
                      gone.
                    </BodyText>
                    <Button
                      onPress={() =>
                        void Linking.openURL(getManagementUrl(info))
                      }
                      bg={C.cardBg}
                      color={C.accent}
                      fontWeight="700"
                      br="$4"
                      h={44}
                      alignSelf="flex-start"
                      px="$4"
                      borderWidth={1}
                      borderColor={C.accent}
                      pressStyle={{ opacity: 0.85 }}
                    >
                      Manage Apple subscription
                    </Button>
                  </SettingsPanelBody>
                </SettingsPanel>
              ) : (
                <SettingsPanel
                  title="Your membership is still active"
                  subtitle="Billed on openstudiojazz.com"
                >
                  <SettingsPanelBody>
                    <BodyText color={C.muted} ta="left">
                      Deleting this account also cancels your website
                      membership and stops future billing.
                    </BodyText>
                  </SettingsPanelBody>
                </SettingsPanel>
              )
            ) : null}

            <SettingsPanel
              title="Confirm"
              footnote={`Type ${CONFIRM_WORD} to enable the button.`}
            >
              <SettingsPanelBody>
                <Input
                  value={confirmation}
                  onChangeText={setConfirmation}
                  placeholder={CONFIRM_WORD}
                  autoCapitalize="characters"
                  autoCorrect={false}
                  bg={C.cardBg}
                  borderColor={confirmed ? C.error : C.border}
                  borderWidth={1}
                  br={10}
                  h={46}
                  color={C.text}
                  maw={280}
                />
                {error ? (
                  <BodyText color={C.error} ta="left">
                    {error}
                  </BodyText>
                ) : null}
              </SettingsPanelBody>
            </SettingsPanel>

            <XStack gap="$3" ai="center" flexWrap="wrap">
              <Button
                onPress={() => void runDelete()}
                disabled={!confirmed || deleting}
                opacity={!confirmed || deleting ? 0.5 : 1}
                bg={C.error}
                color={C.cardBg}
                fontWeight="700"
                br="$4"
                h={44}
                px="$5"
                icon={deleting ? <Spinner color={C.cardBg} /> : undefined}
                pressStyle={{ opacity: 0.85 }}
              >
                Delete my account
              </Button>
              <Button chromeless color={C.muted} onPress={goBack}>
                <CaptionText color={C.muted}>Keep my account</CaptionText>
              </Button>
            </XStack>

            <CaptionText color={C.muted} ta="left">
              Need help instead? Email support@openstudiojazz.com
            </CaptionText>
          </YStack>
        </Container>
        </Pressable>
      </ScrollView>
    </YStack>
  );
}
